import { type FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { orderFeedbackEvent } from '../../tracking/orderFeedbackTracking.ts';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';

import { PdpSharedSellerDetailsModalLazy } from '../PdpSharedSellerDetailsModal/PdpSharedSellerDetailsModalLazy.tsx';

// styles
import styles from './PdpSharedSellerDetailsFeedback.scss';

// types
import { type PdpSharedSellerDetailsFeedback_seller$key as Seller } from './__generated__/PdpSharedSellerDetailsFeedback_seller.graphql.ts';

const sellerFragment = graphql`
    fragment PdpSharedSellerDetailsFeedback_seller on Seller {
        serviceId
        orderFeedbackPdp {
            metadata {
                aggregatedFeedback {
                    publicNoteTotalResults
                }
            }
            edges {
                node {
                    serviceId
                }
            }
        }
    }
`;

const ParenthesisWrapper: FC<{
    children: React.ReactNode;
    isCompact?: boolean;
}> = ({ children, isCompact = false }) => {
    return isCompact ? <>({children})</> : <>{children}</>;
};

export const PdpSharedSellerDetailsFeedback: FC<{
    seller: Seller;
    isCompact?: boolean;
}> = ({ seller: sellerRef, isCompact = false }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const { orderFeedbackPdp: reviews } = seller || {};
    const numberOfReviews = reviews?.metadata?.aggregatedFeedback?.publicNoteTotalResults;
    const [isReviewsModalOpen, setIsReviewModalOpen] = useState(false);

    const openModal = useCallback(() => {
        orderFeedbackEvent({
            action: 'seller reviews expanded',
            label: 'pdp - seller details',
        });
        setIsReviewModalOpen(true);
    }, []);

    const onCloseModal = useCallback(() => {
        setIsReviewModalOpen(false);
    }, []);

    if (!numberOfReviews) {
        return null;
    }

    const reviewsNumberElement = (
        <ParenthesisWrapper isCompact={isCompact}>
            <FormattedMessage
                id="PdpSharedSellerDetailsFeedback.seeReviews"
                defaultMessage="{numberOfReviews, plural, one {# Review} other {# Reviews}}"
                values={{ numberOfReviews }}
            />
        </ParenthesisWrapper>
    );

    if (isCompact) {
        return <span className={styles.reviewsLink}>{reviewsNumberElement}</span>;
    }

    return (
        <>
            <Link
                onClick={openModal}
                className={styles.reviewsLink}
                dataTn="order-feedback-modal-trigger"
            >
                {reviewsNumberElement}
            </Link>
            <PdpSharedSellerDetailsModalLazy
                sellerId={seller.serviceId}
                isOpen={isReviewsModalOpen}
                onClose={onCloseModal}
            />
        </>
    );
};
